
import React, { useState, useEffect } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { ThemeProvider } from "@/contexts/ThemeContext";
import Index from "./pages/Index";
import Auth from "./pages/Auth";
import CompanyRegistration from "./pages/CompanyRegistration";
import Landing from "./pages/Landing";
import Data from "./pages/Data";
import DataCleaning from "./pages/DataCleaning";
import PredictiveModels from "./pages/PredictiveModels";
import Dashboards from "./pages/Dashboards";
import Settings from "./pages/Settings";
import PaymentCallback from "./pages/payment/Callback";
import PaymentSuccess from "./pages/payment/Success";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import TermsOfUse from "./pages/TermsOfUse";
import { FeedbackDialog } from "./components/feedback/FeedbackDialog";
import { CookieConsentBanner } from "./components/legal/CookieConsentBanner";
import { supabase } from "@/lib/supabase";
import Pricing from "./pages/Pricing";
import TeamManagement from "./pages/TeamManagement";
import FolderManagement from "./pages/FolderManagement";
import DynamicDashboards from "./pages/DynamicDashboards";
import AIWorkflows from "./pages/AIWorkflows";
import NotFound from "./pages/NotFound";

const PrivateRoute = ({ children }: { children: React.ReactNode }) => {
  const [session, setSession] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [onboardingComplete, setOnboardingComplete] = useState(false);

  useEffect(() => {
    // First set up auth state listener for immediate response to auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, currentSession) => {
        console.log("Auth state changed in PrivateRoute:", event);
        setSession(currentSession);
        
        if (currentSession) {
          await checkOnboardingStatus(currentSession.user.id);
        } else {
          setOnboardingComplete(false);
        }
        setLoading(false);
      }
    );

    // Then check for existing session
    const getInitialSession = async () => {
      try {
        console.log("Checking for existing session");
        const { data, error } = await supabase.auth.getSession();
        if (error) {
          console.error("Error getting session:", error);
        } else {
          console.log("Session data:", data.session ? "Found" : "Not found");
        }
        setSession(data.session);
        
        if (data.session) {
          await checkOnboardingStatus(data.session.user.id);
        }
      } catch (error) {
        console.error("Session fetch error:", error);
      } finally {
        setLoading(false);
      }
    };

    getInitialSession();

    return () => subscription.unsubscribe();
  }, []);

  const checkOnboardingStatus = async (userId: string) => {
    try {
      // For OAuth users, their profile might be created by trigger with delay
      // Wait a bit longer and retry more times for OAuth flow
      let retries = 5;
      let profile = null;
      
      for (let i = 0; i < retries; i++) {
        const { data, error } = await supabase
          .from("profiles")
          .select("*")
          .eq("id", userId)
          .single();
        
        if (data) {
          profile = data;
          break;
        }
        
        // If this is not the last retry, wait and retry
        if (i < retries - 1) {
          console.log(`Profile not found, retrying in ${i === 0 ? '500ms' : '1s'}... (attempt ${i + 1}/${retries})`);
          await new Promise(resolve => setTimeout(resolve, i === 0 ? 500 : 1000));
        } else {
          console.error("Profile fetch error after all retries:", error);
        }
      }
      
      setOnboardingComplete(!!profile);
      console.log("Onboarding status set:", !!profile, profile ? "- Profile exists" : "- No profile, user needs onboarding");
    } catch (error) {
      console.error("Error checking onboarding status:", error);
      setOnboardingComplete(false);
    }
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
        <p className="mt-2 text-sm text-muted-foreground">Loading your dashboard...</p>
      </div>;
  }

  if (!session) {
    console.log("No session found, redirecting to /auth");
    return <Navigate to="/auth" replace />;
  }

  if (!onboardingComplete) {
    console.log("Onboarding not complete, redirecting to company registration");
    return <Navigate to="/company-registration" replace />;
  }

  return <>{children}</>;
};

// Create QueryClient outside of component to prevent recreation
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      staleTime: 5 * 60 * 1000, // 5 minutes
      refetchOnWindowFocus: false,
    },
  },
});

function App() {
  // Ensure React is available before rendering
  if (!React || typeof React.useState !== 'function') {
    return <div>Loading...</div>;
  }

  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider defaultTheme="system" storageKey="nextwave-ui-theme">
        <Toaster />
        <Sonner />
          <BrowserRouter>
            <Routes>
              {/* Authentication routes */}
              <Route path="/auth" element={<Auth />} />
              <Route path="/company-registration" element={<CompanyRegistration />} />
              
              {/* Main dashboard route - protected, default route */}
              <Route
                path="/"
                element={
                  <PrivateRoute>
                    <Index />
                  </PrivateRoute>
                }
              />
              
              {/* Legacy landing route - redirect to auth for unauthenticated users */}
              <Route path="/landing" element={<Navigate to="/auth" replace />} />
              <Route
                path="/data"
                element={
                  <PrivateRoute>
                    <Data />
                  </PrivateRoute>
                }
              />
              <Route
                path="/data-cleaning"
                element={
                  <PrivateRoute>
                    <DataCleaning />
                  </PrivateRoute>
                }
              />
              <Route
                path="/predictive-models"
                element={
                  <PrivateRoute>
                    <PredictiveModels />
                  </PrivateRoute>
                }
              />
              <Route
                path="/dashboards"
                element={
                  <PrivateRoute>
                    <Dashboards />
                  </PrivateRoute>
                }
              />
              <Route
                path="/settings"
                element={
                  <PrivateRoute>
                    <Settings />
                  </PrivateRoute>
                }
              />
              <Route
                path="/pricing"
                element={
                  <PrivateRoute>
                    <Pricing />
                  </PrivateRoute>
                }
              />
              <Route
                path="/team-management"
                element={
                  <PrivateRoute>
                    <TeamManagement />
                  </PrivateRoute>
                }
              />
              <Route
                path="/folder-management"
                element={
                  <PrivateRoute>
                    <FolderManagement />
                  </PrivateRoute>
                }
              />
              <Route path="/folders" element={<Navigate to="/folder-management" replace />} />
              <Route
                path="/ai-workflows"
                element={
                  <PrivateRoute>
                    <AIWorkflows />
                  </PrivateRoute>
                }
              />
              <Route
                path="/dynamic-dashboards"
                element={
                  <PrivateRoute>
                    <DynamicDashboards />
                  </PrivateRoute>
                }
              />
              <Route path="/payment/callback" element={<PaymentCallback />} />
              <Route path="/payment/success" element={<PaymentSuccess />} />
              
              {/* Legal pages - accessible without authentication */}
              <Route path="/privacy-policy" element={<PrivacyPolicy />} />
              <Route path="/terms-of-use" element={<TermsOfUse />} />
              
              {/* CRM OAuth callback routes - redirect to data page with parameters */}
              <Route 
                path="/hubspot/callback" 
                element={<Navigate to="/data?crm_type=hubspot&oauth_success=true" replace />} 
              />
              <Route 
                path="/zoho/callback" 
                element={<Navigate to="/data?crm_type=zoho&oauth_success=true" replace />} 
              />
              
              {/* 404 page for non-existent routes */}
              <Route path="*" element={<NotFound />} />
            </Routes>
            <React.Suspense fallback={null}>
              <FeedbackDialog />
              <CookieConsentBanner />
            </React.Suspense>
          </BrowserRouter>
      </ThemeProvider>
    </QueryClientProvider>
  );
}

export default App;
